import type { UseFormReturn } from "react-hook-form";

import type { CreateGamificationFormData } from "../validations/gamification.validation";
import type { BannerGallerySelection } from "../utils/banner-source";
import { type BannerSlotState, useBannerSlot } from "./use-banner-slot";

type BannerFormValues = Pick<
  CreateGamificationFormData,
  | "banner16x9"
  | "banner16x9SourceKey"
  | "banner16x9SourcePreview"
  | "banner3x4"
  | "banner3x4SourceKey"
  | "banner3x4SourcePreview"
>;

export interface BannerState {
  cover: BannerSlotState;
  alternative: BannerSlotState;
}

interface InitialBannerUrls {
  banner3x4Url?: string;
  banner16x9Url?: string;
}

const DIRTY = { shouldDirty: true } as const;

const previewOf = (
  selection: BannerGallerySelection | undefined,
): BannerFormValues["banner16x9SourcePreview"] =>
  selection && { url: selection.url, label: selection.label };

export function useBannerState<T extends BannerFormValues>(
  form: UseFormReturn<T>,
  initial?: InitialBannerUrls,
): BannerState {

  const { setValue, watch, clearErrors } =
    form as unknown as UseFormReturn<BannerFormValues>;

  const alternative = useBannerSlot({
    initialUrl: initial?.banner16x9Url,
    sourcePreview: watch("banner16x9SourcePreview"),
    setFile: (file) => {
      setValue("banner16x9", file, DIRTY);
      clearErrors("banner16x9");
    },
    setSource: (selection) => {
      setValue("banner16x9SourceKey", selection?.key, DIRTY);
      setValue("banner16x9SourcePreview", previewOf(selection), DIRTY);
    },
  });

  const cover = useBannerSlot({
    initialUrl: initial?.banner3x4Url,
    sourcePreview: watch("banner3x4SourcePreview"),
    setFile: (file) => {
      setValue("banner3x4", file, DIRTY);
      clearErrors("banner3x4");
    },
    setSource: (selection) => {
      setValue("banner3x4SourceKey", selection?.key, DIRTY);
      setValue("banner3x4SourcePreview", previewOf(selection), DIRTY);
    },
  });

  return { cover, alternative };
}
